import { Node } from '@msdyn365-commerce-modules/utilities';
import { ICoreContext, RichTextComponent} from '@msdyn365-commerce/core';
import {
    Address, CountryRegionInfo, StateProvinceInfo
} from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { observable, reaction, set } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import Modal from 'reactstrap/lib/Modal';
import ModalBody from 'reactstrap/lib/ModalBody';
import ModalHeader from 'reactstrap/lib/ModalHeader';
import { AddressCommon } from '../../../common/address-common';
import { AddressFormat } from '../../../common/address-format';
import { AddressItemType } from '../../../common/address-format.data';
import { AddressMetaData } from '../../../common/address-meta-data';
import {  AddressType, IAddressResource, IAddressResponse } from '../../../common/address-module.data';
import { AddressAddUpdate, IAddressAddItem, IAddressAddUpdateProps } from '../../../common/components/address-add';
import { IShipToAddress, IWineClubMemberData } from '../../../dataActions/DataServiceEntities.g';
import { clubManagementPreferenceMap, wineClubPickupLocations } from '../../../settings/app.settings';
import { BusLoader, LoadBus } from '../../../Utilities/event-bus';
import { IWineClubManagementData } from '../wine-club-management.data';
import { IWineClubManagementProps } from '../wine-club-management.props.autogenerated';

export interface IWineClubEditFormProps {
    membership: IWineClubMemberData;
    managementProps: IWineClubManagementProps<{}>;
    context: ICoreContext;
    actionDeniedLabel: string;
    title: string;
    onDestroy?(): void;
    action(formData: wineClubEditFormData): Promise<void | string>;
}

interface IWineClubEditFormState {
    isFormCompleted: boolean;
    isModalOpen: boolean;
    error?: string;
}

export type wineClubEditFormData = {
    creditCard: string;
    shippingAddress: string;
    deliveryPreference: string;
    pickupLocation: string;
};

/**
 * modaled edit form
 */
@LoadBus('wine-club-management')
@observer
export class WineClubEditForm extends React.Component<IWineClubEditFormProps, IWineClubEditFormState> {

    @observable private _creditCard: string;
    @observable private _shippingAddress: string;
    @observable private _deliveryPreference: string;
    @observable private _pickupLocation: string;

    @observable private isUpdating?: boolean;
    @observable private countryRegionId: string = 'USA';
    @observable private addAddress: Address;
    @observable private validationError: object;
    @observable private addressActionResponse?: IAddressResponse;
    @observable private stateProvinceInfo?: StateProvinceInfo[];
    @observable private shipToAddresses?: IShipToAddress[];
    @observable private hasError?: boolean;

    private bus: BusLoader | undefined;
    private addressCommon: AddressCommon;
    private addressFormat: AddressFormat;
    private countryRegions: CountryRegionInfo[];
    private customerAccountNumber?: string;
    private resources: IAddressResource;

    constructor(props: IWineClubEditFormProps) {
        super(props);
        const { context, resources, telemetry } = this.props.managementProps;

        // default to primary, then use the other items
        // its pretty safe to assume if they reached this form they already have a card so no need to check
        const defaultCard = this.props.membership.CreditCard!.find(card => card.IsPrimary) || this.props.membership.CreditCard!.find(card => !!card);
        this.shipToAddresses = this.props.membership.ShipToAddress;
        const defaultShipping = this.shipToAddresses!.find(address => address.IsPrimary) || this.shipToAddresses!.find(address => !!address);

        this._creditCard = String(defaultCard?.RecId) || '';
        this._shippingAddress = String(defaultShipping?.ShipToAddressRecId) || '';
        this._deliveryPreference = this.props.membership.DeliveryPreference || '';
        this._pickupLocation = this._searchForLocation();

        this._onEditValueChange = this._onEditValueChange.bind(this);

        this._onFinish = this._onFinish.bind(this);
        this._toggle = this._toggle.bind(this);
        this._closeModal = this._closeModal.bind(this);
        this._showAddAddress = this._showAddAddress.bind(this);

        this.state = {
            isFormCompleted: false,
            isModalOpen: false
        };

        const data = this.props.managementProps.data as IWineClubManagementData;
        this.customerAccountNumber = context && context.request && context.request.user && context.request.user.customerAccountNumber;
        this.resources = resources;
        this.addAddress = {};
        this.validationError = {};
        this.countryRegions = data.countryRegions.result || [];
        this.addressFormat = new AddressFormat(context, this.countryRegions, new AddressMetaData({ ...resources }));
        this.addressCommon = new AddressCommon(context, resources, telemetry, this.customerAccountNumber);
        this._toggle = this._toggle.bind(this);
        this._getStateProvinces();
        set(this.addAddress, { ['ThreeLetterISORegionName']: this.countryRegionId });
    }

    public componentDidMount(): void {
        reaction(
            () => this.countryRegionId,
            () => this._getStateProvinces()
        );
    }

    public render(): JSX.Element {
        return (
            <div className={'wine-club-management__modal wine-club-management__modal-edit'}>
                <h4 className='wine-club-management__modal-heading'>{this.props.title}</h4>
                <div className='wine-club-management__modal-container'>
                    <div className='wine-club-management__modal-content'>
                        {
                            this.props.managementProps.config.editFormText &&
                                <RichTextComponent
                                    className='wine-club-management__edit-details'
                                    text={this.props.managementProps.config.editFormText}
                                />
                        }
                        {
                            (!this.state.error) ?
                                this._displayEdit
                                :
                                this._displayError
                        }
                    </div>
                    <Modal
                        className='wine-club-management__modal wine-club-management__modal-add-address'
                        autoFocus={true}
                        fade={true}
                        isOpen={this.state.isModalOpen}
                        horizontalPosition={'center'}
                        verticalPosition={'center'}
                        zIndex={1000}
                        applicationNode={'rsg-root'}
                        toggle={this._closeModal}
                    >
                        <ModalHeader toggle={this._closeModal}>
                            <h4 className='wine-club-management__modal-heading'>Add Address</h4>
                        </ModalHeader>
                        <ModalBody>
                            {this._renderAddressForm(this._getAddressForm())}
                        </ModalBody>
                    </Modal>
                </div>
            </div>
        );
    }

    private get _displayEdit(): JSX.Element {
        const isPickup = this._isPickupPreference();
        return (
            <>
                {/* Hide credit card and shipping method for gift memberships */}
                {!this.props.membership.GiftCustName && this._listCreditCards}
                {!this.props.membership.GiftCustName && this._deliveryPreferences}
                {isPickup && this._pickupLocations}
                {!isPickup && this._listShippingAddresses}
                {!isPickup && <button className='wine-club-management__edit-add-address' onClick={this._showAddAddress}>Add Address</button>}
                <div className='wine-club-management__modal-buttons'>
                    <button
                        className='wine-club-management__modal-button wine-club-management__modal-button-confirm'
                        onClick={this._onFinish}
                    >
                        Save
                    </button>
                    <button
                        className='wine-club-management__modal-button wine-club-management__modal-button-cancel'
                        onClick={this._toggle}
                    >
                        Cancel
                    </button>
                </div>
            </>
        );
    }

    private get _pickupLocations(): JSX.Element {
        const pickupLocations = this.props.managementProps.context.app.config.wineClubPickupLocations as wineClubPickupLocations || [];
        const isMultiLocations = pickupLocations && pickupLocations.length > 1;
        const singleLocation = pickupLocations ? pickupLocations[0] : '';
        return (
            <div className='wine-club-management__selector-group'>
                {isMultiLocations &&
                    <>
                        <label htmlFor='pickup-location'>
                            {this.props.managementProps.resources.wineClubManagement__pickupLocationsLabel}
                        </label>
                        <div className='wine-club-management__edit-pickup'>
                            {
                                pickupLocations.map(
                                    location => (
                                        <div className='wine-club-management__edit-pickup-option' key={location.id}>
                                            <input
                                                className='wine-club-management__edit-pickup-option-input'
                                                name='pickup-location'
                                                value={location.id}
                                                data-name='_pickupLocation'
                                                onChange={this._onEditValueChange}
                                                type='radio'
                                                id={location.id}
                                                checked={this._pickupLocation === location.id ? true : false}
                                            />
                                            <label className='wine-club-management__edit-pickup-option-label' htmlFor={location.id}>
                                                <span className='wine-club-management__edit-pickup-option-name'>{location.name}</span>
                                                <span className='wine-club-management__edit-pickup-option-address'>{location.address}</span>
                                            </label>
                                        </div>
                                    )
                                )
                            }
                        </div>
                    </>
                }
            {!isMultiLocations && singleLocation && (
                <>
                    <div className='wine-club-management__selector-group-label'>
                        {this.props.managementProps.resources.wineClubManagement__pickupLocationsLabel}
                    </div>
                    <div className='wine-club-management__edit-pickup'>
                        <div className='wine-club-management__edit-pickup-single'>
                            <span className='wine-club-management__edit-pickup-single-name'>{singleLocation.name}</span>
                            <span className='wine-club-management__edit-pickup-single-address'>{singleLocation.address}</span>
                        </div>
                    </div>
                </>
            )}
            </div>
        );
    }

    private get _deliveryPreferences(): JSX.Element {
        const prefMap = this._formattedPrefMap();
        return (
            prefMap && <div className='wine-club-management__selector-group'>
                <label htmlFor='delivery-preference'>
                    {this.props.managementProps.resources.wineClubManagement__deliveryPreferenceLabel}
                </label>
                <select
                    className='wine-club-management__edit-delivery wine-club-management__selector'
                    value={this._deliveryPreference}
                    data-name='_deliveryPreference'
                    onChange={this._onEditValueChange}
                    id='delivery-preference'
                >
                    {
                        prefMap.map(preference => <option key={preference.id} value={preference.id} aria-selected={this._deliveryPreference === preference.id} >{preference.shipName}</option>)
                    }
                </select>
            </div>
        );
    }

    private _searchForLocation(): string {
        if (!this._deliveryPreference) { return ''; }
        const prefMap = this.props.context.request.app.config.clubManagementPreferenceMap as clubManagementPreferenceMap;
        const deliveryPref = prefMap.find((pref) => {
            return pref.id === this._deliveryPreference;
        });
        return deliveryPref?.pickupId || '';
    }

    private _formattedPrefMap(): clubManagementPreferenceMap {
        const prefMap = this.props.context.request.app.config.clubManagementPreferenceMap as clubManagementPreferenceMap;
        // we only want shipping preferences
        const formattedPrefMap = prefMap.filter(pref => {
            return !pref.pickupId;
        });
        // and one preference for all pickup locations
        formattedPrefMap.push(
            {
                id: 'PICKUP',
                shipName: 'Pickup',
                pickupId: ''
            }
        );
        return formattedPrefMap;
    }

    private get _listCreditCards(): JSX.Element {
        return (
            <div className='wine-club-management__selector-group'>
                <label htmlFor='credit-cards'>
                    {this.props.managementProps.resources.wineClubManagement__editFormCCLabel}
                </label>
                <select
                    className='wine-club-management__edit-cc wine-club-management__selector'
                    value={this._creditCard}
                    data-name='_creditCard'
                    onChange={this._onEditValueChange}
                    id='credit-cards'
                >
                    {
                        this.props.membership.CreditCard?.map(
                            card => (
                                <option
                                    key={card.RecId}
                                    value={String(card.RecId)}
                                    aria-selected={+this._creditCard === card.RecId}
                                >
                                    ****{card.CreditCardNum}
                                </option>
                            )
                        )
                    };
                </select>
                {this.props.managementProps.config.creditCardFooter &&
                    <RichTextComponent
                        className='wine-club-management__edit-cc-message'
                        text={this.props.managementProps.config.creditCardFooter}
                    />
                }
            </div>
        );
    }

    private get _listShippingAddresses(): JSX.Element {
        const isMultiAdresses = this.shipToAddresses && this.shipToAddresses.length > 1;
        const singleAddress = this.shipToAddresses ? this.shipToAddresses[0] : '';
        return (
            <div className='wine-club-management__selector-group'>
                {isMultiAdresses &&
                    <>
                        <label htmlFor='shipping-addresses'>
                            {this.props.managementProps.resources.wineClubManagement__editFormShippingLabel}
                        </label>
                        <div className='wine-club-management__edit-shipping'>
                            <select
                                className='wine-club-management__edit-shipping wine-club-management__selector'
                                value={this._shippingAddress}
                                data-name='_shippingAddress'
                                onChange={this._onEditValueChange}
                                id='shipping-addresses'
                            >
                                {
                                    this.shipToAddresses?.map(
                                        ship => (
                                            <option
                                                key={ship.ShipToAddressRecId}
                                                value={String(ship.ShipToAddressRecId)}
                                                aria-selected={+this._shippingAddress === ship.ShipToAddressRecId}
                                            >
                                                {this._parseAddress(ship)}
                                            </option>
                                        )
                                    )
                                };
                            </select>
                        </div>
                    </>
                }
                {!isMultiAdresses && singleAddress &&
                    <>
                        <div className='wine-club-management__edit-shipping'>
                            {this.props.managementProps.resources.wineClubManagement__editFormShippingLabel}
                        </div>
                        <div className='wine-club-management__edit-shipping-single'>
                            <span className='wine-club-management__edit-shipping-single-name'>{singleAddress.Name}</span>
                            <span className='wine-club-management__edit-shipping-single-address'>{this._parseAddress(singleAddress)}</span>
                        </div>
                    </>
                }
            </div>
        );
    }

    private _isPickupPreference(): boolean {
        if (this._deliveryPreference === 'PICKUP') { return true; }
        if (this._searchForLocation()) {
            this._deliveryPreference = 'PICKUP';
            return true;
        } else {
            return false;
        }
    }

    private _onEditValueChange(event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void {
        this[event.target.dataset.name!] = event.target.value;

        if (event.target.dataset.name === '_deliveryPreference' && event.target.value === 'PICKUP' && !this._pickupLocation) {
            const pickupLocations = this.props.managementProps.context.app.config.wineClubPickupLocations as wineClubPickupLocations || [];
            const firstLoc = pickupLocations.find(loc => !!loc);
            if (firstLoc) {
                this._pickupLocation = firstLoc.id;
            }
        }
    }

    private get _displayError(): JSX.Element {
        return (
            <>
                <div className='wine-club-management__modal-error'>{this.state.error}</div>
                <div className='wine-club-management__modal-buttons'>
                    <button className='wine-club-management__modal-button wine-club-management__modal-button-cancel' onClick={this._toggle}>{this.props.actionDeniedLabel}</button>
                </div>
            </>
        );
    }

    private _toggle(): void {
        // trigger repull of membership data to load any newly added addresses
        this.bus!.publish('success');
        this.props.onDestroy && this.props.onDestroy();
    }

    private async _onFinish(): Promise<void> {
        // we need to set the correct unique delivery preference on pickups before submit
        let submitDeliveryPref = this._deliveryPreference;
        if (this._deliveryPreference === 'PICKUP') {
            const prefMap = this.props.context.request.app.config.clubManagementPreferenceMap as clubManagementPreferenceMap;
            const pickupPref = prefMap.find(pref => {
                return pref.pickupId === this._pickupLocation;
            });
            if (pickupPref) {
                submitDeliveryPref = pickupPref.id;
            } else {
                submitDeliveryPref = '';
            }
        }

        const response = await this.props.action({
            creditCard: this._creditCard,
            shippingAddress: this._shippingAddress,
            deliveryPreference: submitDeliveryPref,
            pickupLocation: this._pickupLocation
        });
        if (response) {
            this.setState({
                error: response
            });
        } else {
            this.props.onDestroy && this.props.onDestroy();
        }
    }

    private _parseAddress(address: IShipToAddress): string {
        return `${address.StreetAddress} ${address.City}, ${address.State} ${address.ZipCode}`;
    }

    // =========================================================================
    // Add New Address Operations
    // =========================================================================

    private _showAddAddress(): void {
        this.setState({isModalOpen: true});
    }

    private _getAddressForm = (): IAddressAddUpdateProps => {
        return AddressAddUpdate(
            {
                isUpdating: this.isUpdating,
                resources: this.resources,
                addressType: AddressType.Shipping,
                addressFormat: this.addressFormat.getAddressFormat((this.addAddress.ThreeLetterISORegionName) || this.countryRegionId),
                defaultCountryRegionId: this.countryRegionId,
                selectedAddress: this.addAddress,
                validationError: this.validationError,
                addressActionResponse: this.addressActionResponse,
                dropdownDisplayData: this.addressFormat.getPrefilledAddressDropdownData(this.resources.addressStateDefaultSelectionText, this.stateProvinceInfo, true),
                onInputChange: this._onAddressAddInputChange,
                onDropdownChange: this._onAddressAddDropdownChange,
                onSave: this._onAddressAddSubmit,
                onCancel: this._closeModal
            }
        );
    };

    private _renderAddressForm = (props: IAddressAddUpdateProps): JSX.Element => {
        const { AddressForm, heading, items, error, isShowCancelButton, isShowSaveButton, saveButton, cancelButton } = props;
        return (
            <Node {...AddressForm}>
                {heading}
                {items && items.map((item: IAddressAddItem) => {
                    const { AddressItem, key, label, input, alert } = item;

                    return (<Node {...AddressItem} key={key}>
                        {label}
                        {this.hasError && alert}
                        {input}
                    </Node>);
                })}
                {this.hasError && <Node {...error.AddressError}>
                    {error.title}
                    {error.message}
                </Node>}
                {isShowSaveButton && saveButton}
                {isShowCancelButton && cancelButton}
            </Node>
        );
    };

    private _getStateProvinces = (): void => {
        if (!this.countryRegionId) {
            return;
        }

        // tslint:disable-next-line:no-floating-promises
        this.addressCommon.getStateProvinces(this.countryRegionId).then(
            (result: StateProvinceInfo[]) => {
                this.stateProvinceInfo = result;
            }).catch(e => {
                this.props.managementProps.telemetry.exception(e);
            });
    };

    private onAddressAdd = (name: string, value: string) => {
        set(this.addAddress, { [name]: value });
    };

    private _onAddressAddInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const value = (event.target.value || '').replace(new RegExp('[<>]', 'gi'), '');
        this.onAddressAdd(event.target.name, value);
    };

    private _onAddressAddDropdownChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        this.onAddressAdd(event.target.name, event.target.value);

        if (event.target.name === AddressItemType[AddressItemType.ThreeLetterISORegionName]) {
            this._onCountryChange(event.target.value);
        }
    };

    private _onCountryChange = (countryRegionId: string) => {
        this.countryRegionId = countryRegionId;
        set(this.addAddress, { ['ThreeLetterISORegionName']: countryRegionId });
    };

    private _onAddressAddSubmit = (): void => {
        if (!this.addressFormat.validateAddressFormat(this.addAddress, this.validationError, this.countryRegionId)) {
            this.hasError = true;
            return;
        }

        let response: Promise<IAddressResponse>;
        if (this.addressCommon.isAuthenticatedFlow()) {
            response = this.addressCommon.addCustomerAddress(this.addAddress);
        } else {
            response = Promise.resolve({ address: this.addAddress });
        }

        this._onAddressUpdate(response, this._onAddSuccess);
    };

    private _onAddSuccess = (response: IAddressResponse): void => {
        if (response.customerAddresses) {
            const newAddress = response.customerAddresses.find((address) => {
                return (
                    address.Name === this.addAddress.Name &&
                    address.Street === this.addAddress.Street &&
                    address.City === this.addAddress.City &&
                    address.State === this.addAddress.State &&
                    address.ZipCode === this.addAddress.ZipCode &&
                    address.Phone === this.addAddress.Phone
                );
            });
            if (newAddress) {
                this.shipToAddresses?.push(
                    {
                        City: newAddress.City,
                        Company: '',
                        DateBirth: '',
                        IsPrimary: false,
                        Name: newAddress.Name,
                        ShipToAddressRecId: newAddress.RecordId,
                        State: newAddress.State,
                        StreetAddress: newAddress.Street,
                        ZipCode: newAddress.ZipCode
                    }
                );
                this._shippingAddress = newAddress.RecordId!.toString();
            }
        }
        this._closeModal();
    };

    private _onAddressUpdate = (response: Promise<IAddressResponse>, onSuccess: (response: IAddressResponse) => void): void => {
        this.isUpdating = true;
        response.then(
            (result: IAddressResponse) => {
                if (result.address) {
                    this.hasError = false;
                    onSuccess(result);
                } else {
                    this.hasError = true;
                    this.addressActionResponse = result;
                }
                this.isUpdating = false;
            },
            () => {
                this.hasError = true;
                this.isUpdating = false;
            }
        );
    };

    private _closeModal(): void {
        this.setState({
            isModalOpen: false
        });
    }
}